import React, { useState, useMemo } from 'react';
import { HistoryEntry, UserAnswer } from '../types';
import ConfirmationModal from './ConfirmationModal';

interface HistoryScreenProps {
    history: HistoryEntry[];
    onExit: () => void;
    onClear: () => void;
}

const HistoryScreen: React.FC<HistoryScreenProps> = ({ history, onExit, onClear }) => {
    const [isClearModalOpen, setIsClearModalOpen] = useState(false);

    const performanceData = useMemo(() => {
        if (history.length === 0) return null;

        const domainStats: { [domain: string]: { correct: number; total: number } } = {};
        let hasDomains = false;
        
        history.forEach(entry => {
            entry.questions.forEach(q => {
                const domain = q.domain || 'Não categorizado';
                if (q.domain) hasDomains = true;
                
                if (!domainStats[domain]) {
                    domainStats[domain] = { correct: 0, total: 0 };
                }
                domainStats[domain].total++;
                const userAnswer = entry.answers.get(q.id);
                // FIX: Explicitly type userAnswer as UserAnswer to access isCorrect property.
                if ((userAnswer as UserAnswer)?.isCorrect) {
                    domainStats[domain].correct++;
                }
            });
        });

        if (!hasDomains) return null;

        const performanceArray = Object.entries(domainStats).map(([domain, stats]) => {
            const percentage = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
            return { domain, ...stats, percentage };
        });

        if(performanceArray.length === 0) return null;

        const sortedPerformance = [...performanceArray].sort((a, b) => a.percentage - b.percentage);
        const weakest = sortedPerformance[0];
        const strongest = sortedPerformance[sortedPerformance.length - 1];

        return {
            performanceByDomain: performanceArray,
            weakest,
            strongest,
        };
    }, [history]);

    const formatDateTime = (isoString: string) => {
        const date = new Date(isoString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    const formatTime = (totalSeconds: number) => {
        if (isNaN(totalSeconds) || totalSeconds < 0) return '00:00';
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    const handleConfirmClear = () => {
        onClear();
        setIsClearModalOpen(false);
    };

    const getBarColor = (percentage: number) => {
        if (percentage >= 72) return 'bg-green-500';
        if (percentage >= 50) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    return (
        <>
            <ConfirmationModal
                isOpen={isClearModalOpen}
                onConfirm={handleConfirmClear}
                onCancel={() => setIsClearModalOpen(false)}
                title="Limpar Histórico?"
            >
                Tem certeza de que deseja apagar todo o seu histórico de exames? Esta ação não pode ser desfeita.
            </ConfirmationModal>
            
            <div className="w-full max-w-7xl mx-auto animate-fade-in">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Painel de Desempenho</h2>
                    <button onClick={onExit} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                        <i className="fas fa-arrow-left mr-2"></i>
                        Voltar
                    </button>
                </div>

                {history.length === 0 ? (
                    <div className="bg-white dark:bg-slate-800 p-8 rounded-lg shadow-2xl text-center">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Nenhum Histórico Encontrado</h3>
                        <p className="text-gray-600 dark:text-slate-400">Você ainda não completou nenhum simulado. Seu progresso e análise de desempenho aparecerão aqui.</p>
                    </div>
                ) : (
                    <>
                        {performanceData && (
                             <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-2xl mb-8">
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Resumo Geral</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    <div className="bg-gray-50 dark:bg-slate-900/50 p-4 rounded-lg text-center">
                                        <h4 className="text-lg font-semibold text-green-400 mb-2">Ponto Mais Forte</h4>
                                        <p className="text-xl font-bold">{performanceData.strongest.domain}</p>
                                        <p className="text-gray-600 dark:text-slate-400">{performanceData.strongest.percentage}% de acerto</p>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-slate-900/50 p-4 rounded-lg text-center">
                                        <h4 className="text-lg font-semibold text-red-400 mb-2">Área para Focar</h4>
                                        <p className="text-xl font-bold">{performanceData.weakest.domain}</p>
                                        <p className="text-gray-600 dark:text-slate-400">{performanceData.weakest.percentage}% de acerto</p>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">Desempenho por Domínio</h4>
                                     <div className="space-y-4">
                                        {performanceData.performanceByDomain.map(p => (
                                            <div key={p.domain}>
                                                <div className="flex justify-between items-center mb-1 text-gray-700 dark:text-slate-300">
                                                    <span>{p.domain}</span>
                                                    <span>{p.correct}/{p.total} ({p.percentage}%)</span>
                                                </div>
                                                <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
                                                    <div className={`${getBarColor(p.percentage)} h-2.5 rounded-full`} style={{ width: `${p.percentage}%` }}></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-between items-center mb-4">
                             <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Histórico de Tentativas</h3>
                             <button onClick={() => setIsClearModalOpen(true)} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition-colors text-sm">
                                <i className="fas fa-trash-alt mr-2"></i>
                                Limpar Histórico
                            </button>
                        </div>
                        <div className="space-y-4">
                            {history.map((entry, index) => {
                                const didPass = entry.score >= (typeof (entry as any).passingScore === 'number' && (entry as any).passingScore > 0 ? (entry as any).passingScore : 720);
                                const correctAnswers = Array.from(entry.answers.values()).filter((a: UserAnswer) => a.isCorrect).length;
                                const modeText = entry.customConfig ? 'Personalizado' : (entry.mode === 'practice' ? 'Modo Prática' : 'Modo Simulado');
                                const modeColor = entry.customConfig ? 'text-purple-400' : (entry.mode === 'practice' ? 'text-orange-400' : 'text-cyan-400');

                                return (
                                    <div key={index} className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-lg flex flex-col sm:flex-row items-center justify-between gap-4">
                                        <div className="flex-1 text-center sm:text-left">
                                            <div className="text-sm text-gray-500 dark:text-slate-400">{formatDateTime(entry.date)}</div>
                                            <div className={`font-bold text-lg ${modeColor}`}>{modeText}</div>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-grow-[2] text-center">
                                            <div>
                                                <div className="text-xs text-gray-500 dark:text-slate-400">Corretas</div>
                                                <div className="font-bold text-lg text-green-400">{correctAnswers}/{entry.questions.length}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-gray-500 dark:text-slate-400">Tempo</div>
                                                <div className="font-bold text-lg font-mono">{formatTime(entry.timeTaken)}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-gray-500 dark:text-slate-400">Status</div>
                                                <div className={`font-bold text-lg ${didPass ? 'text-green-400' : 'text-red-400'}`}>
                                                    {didPass ? 'Aprovado' : 'Reprovado'}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-gray-500 dark:text-slate-400">Pontuação</div>
                                                <div className={`font-bold text-2xl ${didPass ? 'text-green-400' : 'text-red-400'}`}>{entry.score}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default HistoryScreen;
